import { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    Typography,
    Grid,
    Card,
    Stack,
    Chip,
    Divider,
    Button,
    Tooltip,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha } from '@mui/material/styles';
import CombatDialog from '../components/CombatDialog';
import DeckOverview from '../components/DeckOverview';
import { UserContext } from '../contexts/UserContext';
import { getCardStats } from '../utilities';
import { getNextLeagueInfo, ARENA_LEAGUES } from '../config/arenaLeagues';
import ArenaLeaderBoard from '../components/ArenaLeaderBoard';
import ArenaWinsEvent from '../components/ArenaWinsEvent';

// 🔥 Centralized Firebase (same app for auth + db)
import { db, auth } from '../firebase';
import {
    addDoc,
    arrayUnion,
    collection,
    doc,
    getDoc,
    getDocs,
    limit as qlimit,
    onSnapshot,
    query,
    runTransaction,
    serverTimestamp,
    Timestamp,
    updateDoc,
    where,
} from 'firebase/firestore';

// 🔥 Firebase Storage for preloading art
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

// How many opponents to show per page (5x1 column)
const OPPONENTS_PER_PAGE = 5;

// How long the arena lockout lasts after a refresh (in ms)
const ARENA_AUTO_REFRESH_MS = 10 * 60 * 1000; // 10 minutes

// 💀 Soul Dust cost to reroll Arena opponents
const SOUL_DUST_REFRESH_COST = 25;

// How many opponents to keep in the full pool (for pagination/random selection)
const OPPONENT_POOL_TARGET = OPPONENTS_PER_PAGE * 4;

// ---------- Gen2 Cloud Run URLs (Arena) ----------
const CF_BASES = {
    claimArenaDailyReward: 'https://claimarenadailyreward-qfihkipuza-uc.a.run.app',
    getArenaLeaderboard: 'https://getarenaleaderboard-qfihkipuza-uc.a.run.app',
};

// Small helper to retry on transient 429s with jitter (copied from Shop)
async function fetchWithRetry(url, options = {}, retries = 2) {
    let attempt = 0;
    let lastErr;
    while (attempt <= retries) {
        try {
            const res = await fetch(url, { ...options, credentials: 'omit' });
            if (!res.ok) {
                let body = '';
                try {
                    body = await res.text();
                } catch { }
                const err = new Error(`HTTP ${res.status}${body ? ` – ${body}` : ''}`);
                err.status = res.status;
                throw err;
            }
            return res;
        } catch (e) {
            lastErr = e;
            const status = e?.status;
            if (status === 429 || status === 503 || status === 504) {
                const backoff = 250 + Math.random() * 400 * (attempt + 1);
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, backoff));
                attempt += 1;
                continue;
            }
            break;
        }
    }
    throw lastErr;
}

// ====== THEME TOKENS (Beasts of Legend) ======
const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';

// Shared “glass” panel styling
const panelSx = {
    background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
    color: TEXT_WARM,
    borderRadius: 2,
    border: `1px solid ${alpha(GOLD, 0.35)}`,
    boxShadow: `0 0 0 1px ${alpha(GOLD, 0.15)} inset, 0 10px 30px ${alpha(GOLD, 0.08)}`,
};

// Gold button
const GoldButton = styled(Button)(() => ({
    fontWeight: 700,
    letterSpacing: '.6px',
    color: TEXT_WARM,
    borderRadius: 12,
    padding: '10px 14px',
    background: `linear-gradient(180deg, ${alpha(GOLD, 0.9)} 0%, ${alpha(GOLD, 0.6)} 100%)`,
    boxShadow: `0 8px 24px ${alpha(GOLD, 0.25)}`,
    textTransform: 'none',
    '&:hover': {
        boxShadow: `0 10px 28px ${alpha(GOLD, 0.35)}`,
        filter: 'brightness(1.05)',
        background: `linear-gradient(180deg, ${alpha(GOLD, 1)} 0%, ${alpha(GOLD, 0.75)} 100%)`,
    },
    '&:disabled': {
        color: alpha(TEXT_WARM, 0.6),
        background: alpha(GOLD, 0.25),
        boxShadow: 'none',
    },
}));

// Gold chip styles (solid + outline variants)
const chipSolidSx = {
    ...panelSx,
    px: 1.25,
    borderColor: alpha(GOLD, 0.5),
    background: alpha(GOLD, 0.18),
    color: TEXT_WARM,
    fontWeight: 600,
};

const chipOutlineSx = {
    px: 1.25,
    color: TEXT_WARM,
    borderColor: alpha(GOLD, 0.55),
    background: alpha('#000', 0.3),
    fontWeight: 600,
};

// Opponent Card
const OpponentCard = styled(Card)(({ theme }) => ({
    position: 'relative',
    cursor: 'pointer',
    padding: theme.spacing(2),
    borderRadius: 14,
    background: 'linear-gradient(180deg, #050505 0%, #0b0b0b 100%)',
    color: TEXT_WARM,
    border: `1.5px solid ${alpha(GOLD, 0.55)}`,
    boxShadow: `
    0 0 0 1px ${alpha(GOLD, 0.12)} inset,
    0 12px 30px ${alpha(GOLD, 0.10)},
    0 2px 10px rgba(0,0,0,0.6)
  `,
    transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease, opacity .18s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `
      0 0 0 1px ${alpha(GOLD, 0.18)} inset,
      0 18px 36px ${alpha(GOLD, 0.16)},
      0 4px 14px rgba(0,0,0,0.7)
    `,
        borderColor: GOLD,
    },
}));

// 🕒 Helper to format countdown from milliseconds
const formatCountdown = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    return `${pad(minutes)}:${pad(seconds)}`;
};

// Simple Fisher–Yates shuffle
function shuffleArray(arr) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = copy[i];
        copy[i] = copy[j];
        copy[j] = tmp;
    }
    return copy;
}

// number formatter
const fmt = (n) => Number(n ?? 0).toLocaleString();

/* =========================================================
   🔥 ARENA-LEVEL IMAGE PRELOAD HELPERS (like CampaignPanel)
   ========================================================= */

const imageUrlCacheArena = new Map(); // storagePath -> downloadURL

const evoClamp = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 1;
    return Math.max(1, Math.min(3, x));
};

const buildCanonicalPath = (cardId, evolutionStage) => {
    const evo = evoClamp(evolutionStage);
    return `cards/card-images/${cardId}_evo${evo}.png`;
};

const getCardImageKey = (card) => {
    if (!card?.cardId) return null;
    const evo = evoClamp(card?.evolutionStage || 1);
    return `${card.cardId}-evo${evo}`;
};

const getUrlForStoragePathArena = async (path) => {
    if (imageUrlCacheArena.has(path)) return imageUrlCacheArena.get(path);
    const storage = getStorage();
    const url = await getDownloadURL(ref(storage, path));
    imageUrlCacheArena.set(path, url);
    return url;
};

const candidatePathsForArenaCard = (card, baseCard) => {
    const evo = evoClamp(card?.evolutionStage || baseCard?.evolutionStage || 1);
    const cardId = card?.cardId || baseCard?.cardId || baseCard?.id;

    const paths = [];
    if (baseCard && baseCard.images && baseCard.images[`evo${evo}`]) {
        paths.push(baseCard.images[`evo${evo}`]);
    }
    if (baseCard && typeof baseCard.image === 'string' && baseCard.image) {
        paths.push(baseCard.image);
    }
    if (cardId) {
        paths.push(buildCanonicalPath(cardId, evo));
    }

    const dedup = [...new Set(paths)];
    if (cardId) dedup.push(buildCanonicalPath(cardId, 1)); // fallback evo1
    return { list: dedup, cardId };
};

const resolveCardImageUrlArena = async (card, baseCard) => {
    const { list, cardId } = candidatePathsForArenaCard(card, baseCard);
    if (!cardId) throw new Error('Missing cardId for arena image resolution.');

    let lastErr = null;
    for (const p of list) {
        try {
            const url = await getUrlForStoragePathArena(p);
            return url;
        } catch (e) {
            lastErr = e;
        }
    }
    throw lastErr || new Error('Unable to resolve image URL (arena).');
};

const preloadImageTag = (src) =>
    new Promise((resolve) => {
        if (!src || typeof Image === 'undefined') return resolve();
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });

// 🕒 Separate component so only this re-renders every second (avoids card flicker)
function DailyCountdownChip({ nextDailyAt }) {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!nextDailyAt) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [nextDailyAt]);

    if (!nextDailyAt || nextDailyAt <= now) {
        return (
            <Chip
                label="Daily Reward: Ready!"
                sx={{ ...chipOutlineSx, ml: 1 }}
                size="small"
            />
        );
    }

    const diff = nextDailyAt - now;
    const label = `Daily Reward: ${formatCountdown(diff)}`;

    return (
        <Chip
            label={label}
            sx={{ ...chipOutlineSx, ml: 1 }}
            size="small"
        />
    );
}

// 🕒 Arena refresh countdown chip
function AutoRefreshCountdownChip({ nextRefreshAt }) {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!nextRefreshAt) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [nextRefreshAt]);

    if (!nextRefreshAt) return null;

    const diff = nextRefreshAt - now;
    const label =
        diff <= 0
            ? 'Arena refresh: Ready'
            : `Arena refresh: ${formatCountdown(diff)}`;

    return (
        <Chip
            label={label}
            sx={{ ...chipOutlineSx }}
            size="small"
        />
    );
}

function Arena() {
    const { userData, globalCards, loading } = useContext(UserContext) || {};

    // Full opponent pool (larger than a single page), page index, beaten opponents for current pool
    const [allOpponents, setAllOpponents] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [beatenOpponents, setBeatenOpponents] = useState(() => new Set());

    const [selectedEnemy, setSelectedEnemy] = useState(null);
    const [loadingOpponents, setLoadingOpponents] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [liveUser, setLiveUser] = useState(null); // live /users/{uid} snapshot

    const [rewardsOpen, setRewardsOpen] = useState(false);

    // 🕒 Daily reward timing state
    const [nextDailyAt, setNextDailyAt] = useState(null);
    const [claimingDaily, setClaimingDaily] = useState(false);
    const [dailyError, setDailyError] = useState(null);

    // 🕒 Arena refresh timing state (driven by Firestore: arenaRefreshAt)
    const [nextRefreshAt, setNextRefreshAt] = useState(null);

    // 🏰 Player guild state
    const [playerGuildName, setPlayerGuildName] = useState(null);
    const [playerGuildLoading, setPlayerGuildLoading] = useState(false);

    // 🌐 Global leaderboard / rank state (from backend)
    const [globalRank, setGlobalRank] = useState(null);
    const [globalPlayerCount, setGlobalPlayerCount] = useState(0);
    const [leaderboardOpen, setLeaderboardOpen] = useState(false);
    const [leaderboardRows, setLeaderboardRows] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [leaderboardError, setLeaderboardError] = useState(null);

    // 🔥 Preloaded images for ALL opponents in pool (+ player deck)
    const [preloadedImages, setPreloadedImages] = useState({});
    const preloadSignatureRef = useRef(null);

    // Player trophies/league (prefer live snapshot; always derive league from trophies)
    const trophies = (liveUser?.trophies ?? userData?.trophies) ?? 0;
    const { league, next, toNext } = getNextLeagueInfo(trophies);
    const displayLeague = league.name;

    // 💀 Soul Dust amount (prefer live snapshot)
    const soulDust = Number(
        (liveUser?.consumables?.soulDust ??
            userData?.consumables?.soulDust ??
            0) || 0
    );

    // Derived current page of opponents
    const pvpOpponents = useMemo(() => {
        const start = currentPage * OPPONENTS_PER_PAGE;
        return allOpponents.slice(start, start + OPPONENTS_PER_PAGE);
    }, [allOpponents, currentPage]);

    const totalPages = Math.max(1, Math.ceil(allOpponents.length / OPPONENTS_PER_PAGE));

    // --- Initialize nextDailyAt from initial userData (in case snapshot is slow)
    useEffect(() => {
        const raw =
            userData?.arenaDailyNextAt ||
            userData?.arenaDaily?.nextAt ||
            null;

        if (!raw) return;

        if (typeof raw.toMillis === 'function') {
            setNextDailyAt(raw.toMillis());
        } else if (typeof raw === 'number') {
            setNextDailyAt(raw);
        }
    }, [userData?.arenaDailyNextAt, userData?.arenaDaily?.nextAt]);

    // --- Initialize arenaRefreshAt from initial userData
    useEffect(() => {
        const raw =
            userData?.arenaRefreshAt ||
            userData?.arena?.refreshAt ||
            null;

        if (!raw) return;

        if (typeof raw.toMillis === 'function') {
            setNextRefreshAt(raw.toMillis());
        } else if (typeof raw === 'number') {
            setNextRefreshAt(raw);
        }
    }, [userData?.arenaRefreshAt, userData?.arena?.refreshAt]);

    // --- Live subscribe to /users/{uid}
    useEffect(() => {
        const uid = auth?.currentUser?.uid || userData?.uid;
        if (!uid) return;
        const userRef = doc(db, 'users', uid);
        const unsub = onSnapshot(userRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            setLiveUser({ uid, ...data });

            const rawDaily =
                data.arenaDailyNextAt ||
                (data.arenaDaily && data.arenaDaily.nextAt) ||
                null;

            if (rawDaily) {
                if (typeof rawDaily.toMillis === 'function') {
                    setNextDailyAt(rawDaily.toMillis());
                } else if (typeof rawDaily === 'number') {
                    setNextDailyAt(rawDaily);
                } else {
                    setNextDailyAt(null);
                }
            } else {
                setNextDailyAt(null);
            }

            const rawRefresh =
                data.arenaRefreshAt ||
                (data.arena && data.arena.refreshAt) ||
                null;

            if (rawRefresh) {
                if (typeof rawRefresh.toMillis === 'function') {
                    setNextRefreshAt(rawRefresh.toMillis());
                } else if (typeof rawRefresh === 'number') {
                    setNextRefreshAt(rawRefresh);
                } else {
                    setNextRefreshAt(null);
                }
            } else {
                setNextRefreshAt(null);
            }
        });
        return () => unsub();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.currentUser?.uid, userData?.uid]);

    // Keep beatenOpponents Set in sync with Firestore arenaBeatenOpponents []
    useEffect(() => {
        const source =
            liveUser?.arenaBeatenOpponents ??
            userData?.arenaBeatenOpponents ??
            [];
        if (!Array.isArray(source)) return;
        setBeatenOpponents(new Set(source));
    }, [liveUser?.arenaBeatenOpponents, userData?.arenaBeatenOpponents]);

    // ---------- Guild name ----------
    useEffect(() => {
        const guildId = liveUser?.guildId ?? userData?.guildId;

        if (!guildId) {
            setPlayerGuildName(null);
            return;
        }

        let cancelled = false;
        setPlayerGuildLoading(true);

        const guildRef = doc(db, 'guilds', guildId);

        (async () => {
            try {
                const snap = await getDoc(guildRef);
                if (!snap.exists()) {
                    if (!cancelled) setPlayerGuildName(null);
                    return;
                }
                const data = snap.data() || {};
                if (!cancelled) setPlayerGuildName(data.name || null);
            } catch (err) {
                console.error('[Arena] failed to load guild name:', err);
                if (!cancelled) setPlayerGuildName(null);
            } finally {
                if (!cancelled) setPlayerGuildLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [liveUser?.guildId, userData?.guildId]);

    // ---------- Helpers ----------
    const hydrateInstance = useCallback((inst) => {
        const base = globalCards?.[inst.cardId];
        if (!base) return null;
        const stats = getCardStats(inst, base);
        const evo = base.evolutions?.[inst.evolutionStage] || {};
        return {
            ...inst,
            ...base,
            attack: stats.attack,
            defense: stats.defense,
            image: evo.image || base.image,
            rarity: evo.rarity || base.rarity,
            ability: evo.ability || base.ability,
            abilityDescription: evo.abilityDescription || base.abilityDescription,
            evolutionStage: inst.evolutionStage ?? 1,
            triggered: false,
        };
    }, [globalCards]);

    const sumDefense = (cards = []) => cards.reduce((s, c) => s + (c?.defense || 0), 0);

    // Player active (attack) deck — hydrated for CombatDialog
    const activeCards = useMemo(() => {
        if (!userData || !globalCards) return [];
        return (userData.ownedCards || [])
            .filter((card) => card.active)
            .map(hydrateInstance)
            .filter(Boolean);
    }, [userData?.ownedCards, globalCards, hydrateInstance]);

    // Pick enemy defense deck from PUBLIC mirror
    const pickAndHydrateDefenseDeck = useCallback((uDoc) => {
        const rawDefense = Array.isArray(uDoc.defenseDeckPublic) ? uDoc.defenseDeckPublic : [];
        const cards = rawDefense.map(hydrateInstance).filter(Boolean).slice(0, 5);
        return cards;
    }, [hydrateInstance]);

    // Fallback when composite index not created
    const fetchOpponentsNoIndex = useCallback(async (band) => {
        const usersRef = collection(db, 'publicUsers');
        const minT = Math.max(0, trophies - band);
        const maxT = Math.max(0, trophies + band);

        const snap = await getDocs(query(
            usersRef,
            where('trophies', '>=', minT),
            where('trophies', '<=', maxT),
            qlimit(50)
        ));

        const round = [];
        const uid = auth?.currentUser?.uid || userData?.uid;
        snap.forEach((d) => {
            if (d.id === uid) return;
            const data = d.data();
            if (data.league === displayLeague) {
                round.push({ userId: d.id, ...data });
            }
        });
        return round;
    }, [trophies, displayLeague, userData]);

    // Query same-league public users within an expanding trophy band
    const fetchOpponents = useCallback(async () => {
        if (!userData || !globalCards) return;
        setLoadingOpponents(true);

        try {
            const uid = auth?.currentUser?.uid || userData?.uid;
            const usersRef = collection(db, 'publicUsers');

            const desiredPool = OPPONENT_POOL_TARGET;
            let band = 300;
            const maxBand = 5000;
            let pool = [];

            while (pool.length < desiredPool && band <= maxBand) {
                const minT = Math.max(0, trophies - band);
                const maxT = Math.max(0, trophies + band);

                try {
                    const qOpp = query(
                        usersRef,
                        where('league', '==', displayLeague),
                        where('trophies', '>=', minT),
                        where('trophies', '<=', maxT),
                        qlimit(50)
                    );

                    const snap = await getDocs(qOpp);
                    const round = [];
                    snap.forEach((d) => {
                        if (d.id === uid) return;
                        round.push({ userId: d.id, ...d.data() });
                    });

                    const seen = new Set(pool.map((p) => p.userId));
                    for (const r of round) if (!seen.has(r.userId)) pool.push(r);
                } catch (err) {
                    const message = String(err?.message || '');
                    if (message.includes('requires an index')) {
                        const round = await fetchOpponentsNoIndex(band);
                        const seen = new Set(pool.map((p) => p.userId));
                        for (const r of round) if (!seen.has(r.userId)) pool.push(r);
                    } else {
                        throw err;
                    }
                }

                band *= 2;
            }

            if (pool.length === 0) {
                setAllOpponents([]);
                setCurrentPage(0);
                return;
            }

            // Build a map of guildId -> guildName for all opponents that have a guildId
            const guildMap = {};
            const uniqueGuildIds = Array.from(
                new Set(
                    pool
                        .map((u) => u.guildId)
                        .filter((id) => typeof id === 'string' && id.length > 0)
                )
            );

            for (const gid of uniqueGuildIds) {
                try {
                    const gRef = doc(db, 'guilds', gid);
                    const gSnap = await getDoc(gRef);
                    if (gSnap.exists()) {
                        const gData = gSnap.data() || {};
                        guildMap[gid] = gData.name || 'No Guild';
                    } else {
                        guildMap[gid] = 'No Guild';
                    }
                } catch (e) {
                    console.error('[Arena] failed fetching guild for opponent:', gid, e);
                    guildMap[gid] = 'No Guild';
                }
            }

            const hydrated = pool.map((u) => {
                const cards = pickAndHydrateDefenseDeck(u);
                if (!cards.length) return null;

                const guildName =
                    (u.guildId && guildMap[u.guildId]) ||
                    u.guildName ||
                    'No Guild';

                return {
                    enemyId: u.userId,
                    username: u.username || 'Opponent',
                    guild: guildName,
                    trophies: u.trophies || 0,
                    league: u.league || 'Unranked',
                    cards,
                    powerLevel: sumDefense(cards),
                };
            }).filter(Boolean);

            const randomized = shuffleArray(hydrated);

            setAllOpponents(randomized);
            setCurrentPage(0);
        } catch (err) {
            console.error('[Arena] fetchOpponents error:', err);
            setAllOpponents([]);
            setCurrentPage(0);
        } finally {
            setLoadingOpponents(false);
        }
    }, [userData, globalCards, trophies, displayLeague, pickAndHydrateDefenseDeck, fetchOpponentsNoIndex]);

    // ---- Battle resolve via Firestore request (no callable) ----
    const handleBattleResolved = useCallback(async (result) => {
        if (!result) {
            console.warn('[Arena] handleBattleResolved called with no result');
            return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
            console.warn('[Arena] handleBattleResolved called with no currentUser');
            return;
        }

        const actorId = currentUser.uid;
        const enemyId =
            result.enemyId ??
            selectedEnemy?.enemyId ??
            selectedEnemy?.userId ??
            selectedEnemy?.uid ??
            null;

        if (!enemyId) {
            console.error('[Arena] No enemyId in handleBattleResolved – aborting battleRequest + beaten update.');
            return;
        }

        const playerWins = !!result.playerWins;
        const winnerPower = Number.isFinite(Number(result.winnerPower))
            ? Number(result.winnerPower)
            : 0;
        const loserPower = Number.isFinite(Number(result.loserPower))
            ? Number(result.loserPower)
            : 0;

        if (playerWins) {
            setBeatenOpponents((prev) => {
                const nextSet = new Set(prev);
                nextSet.add(enemyId);
                return nextSet;
            });

            try {
                const userRef = doc(db, 'users', actorId);
                await updateDoc(userRef, {
                    arenaBeatenOpponents: arrayUnion(enemyId),
                });
            } catch (e) {
                console.error('[Arena] failed to persist beaten opponent:', e);
            }
        }

        try {
            await addDoc(collection(db, 'battleRequests'), {
                actorId,
                enemyId,
                playerWins,
                winnerPower,
                loserPower,
                createdAt: serverTimestamp(),
                status: 'pending',
            });
        } catch (e) {
            console.error('[Arena] battle request error:', e);
        }
    }, [selectedEnemy]);

    // Initial fetch + whenever user’s league/trophies or cards change
    useEffect(() => {
        if (!userData || !globalCards) return;
        fetchOpponents();
    }, [userData?.league, userData?.trophies, globalCards, fetchOpponents]);

    /* =========================================================
       🔥 PRELOAD ALL OPPONENT + PLAYER CARD ART (no battle delay)
       ========================================================= */
    useEffect(() => {
        if (!globalCards) return;
        if (!allOpponents?.length && !activeCards?.length) return;

        // Collect unique card keys across ALL opponents in pool + player active deck
        const uniqueCardsByKey = new Map();

        // Player
        (activeCards || []).slice(0, 5).forEach((c) => {
            const k = getCardImageKey(c);
            if (k && !uniqueCardsByKey.has(k)) uniqueCardsByKey.set(k, c);
        });

        // All opponents in pool (not just current page)
        (allOpponents || []).forEach((op) => {
            (op?.cards || []).slice(0, 5).forEach((c) => {
                const k = getCardImageKey(c);
                if (k && !uniqueCardsByKey.has(k)) uniqueCardsByKey.set(k, c);
            });
        });

        const keys = Array.from(uniqueCardsByKey.keys()).sort();
        const signature = keys.join('|');

        // If nothing changed, skip
        if (preloadSignatureRef.current === signature) return;
        preloadSignatureRef.current = signature;

        let cancelled = false;

        // Concurrency-limited preload (prevents spiking Storage too hard)
        const BATCH = 12;

        (async () => {
            try {
                const entries = Array.from(uniqueCardsByKey.entries()); // [key, card]
                for (let i = 0; i < entries.length; i += BATCH) {
                    if (cancelled) return;

                    const chunk = entries.slice(i, i + BATCH);

                    // eslint-disable-next-line no-await-in-loop
                    const results = await Promise.all(
                        chunk.map(async ([key, card]) => {
                            try {
                                const base = globalCards?.[card.cardId];
                                if (!base) return null;
                                const url = await resolveCardImageUrlArena(card, base);
                                await preloadImageTag(url);
                                return [key, url];
                            } catch (e) {
                                // non-fatal
                                return null;
                            }
                        })
                    );

                    if (cancelled) return;

                    const updates = {};
                    for (const r of results) {
                        if (!r) continue;
                        const [k, url] = r;
                        updates[k] = url;
                    }

                    if (Object.keys(updates).length) {
                        setPreloadedImages((prev) => ({ ...prev, ...updates }));
                    }
                }
            } catch (e) {
                console.warn('[Arena] preload all opponents art error:', e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [allOpponents, activeCards, globalCards]);

    // Helper passed to CombatDialog to retrieve imageSrc per card
    const getImageForCard = useCallback(
        (card) => {
            const key = getCardImageKey(card);
            if (!key) return undefined;
            return preloadedImages[key];
        },
        [preloadedImages]
    );

    const handleEnemyClick = (enemy) => {
        if (!auth?.currentUser?.uid) {
            console.warn('[Arena] Clicked enemy while unauthenticated; ignoring.');
            return;
        }
        if (beatenOpponents.has(enemy.enemyId)) return;
        setSelectedEnemy(enemy);
    };

    const handleCloseDialog = () => setSelectedEnemy(null);

    // Is arena refresh currently free?
    const refreshReady = !nextRefreshAt || nextRefreshAt <= Date.now();

    // 🔁 Refresh opponents. If arenaRefreshAt is in the past, refresh is FREE.
    // Otherwise, charge Soul Dust and set a new cooldown.
    const handleRefresh = async () => {
        if (!userData) return;
        setRefreshing(true);

        try {
            const uid = auth?.currentUser?.uid || userData?.uid;
            if (!uid) throw new Error('No current user');

            const userRef = doc(db, 'users', uid);
            const now = Date.now();

            await runTransaction(db, async (tx) => {
                const snap = await tx.get(userRef);
                if (!snap.exists()) throw new Error('User not found');

                const data = snap.data() || {};
                const consumables = { ...(data.consumables || {}) };

                const rawRefresh =
                    data.arenaRefreshAt ||
                    (data.arena && data.arena.refreshAt) ||
                    null;

                let isFree = true;
                if (rawRefresh && typeof rawRefresh.toMillis === 'function') {
                    isFree = rawRefresh.toMillis() <= now;
                } else if (typeof rawRefresh === 'number') {
                    isFree = rawRefresh <= now;
                }

                const updates = {
                    arenaBeatenOpponents: [],
                    arenaRefreshAt: Timestamp.fromMillis(now + ARENA_AUTO_REFRESH_MS),
                };

                if (!isFree) {
                    const have = Number(consumables.soulDust || 0);
                    if (have < SOUL_DUST_REFRESH_COST) {
                        throw new Error('Not enough Soul Dust');
                    }
                    consumables.soulDust = have - SOUL_DUST_REFRESH_COST;
                    updates.consumables = consumables;
                }

                tx.update(userRef, updates);
            });

            setBeatenOpponents(new Set());
            await fetchOpponents();
        } catch (e) {
            console.error('[Arena] refresh error:', e);
        } finally {
            setRefreshing(false);
        }
    };

    const formatReward = (reward) => {
        if (!reward) return '—';
        const parts = [];
        const sd = Number(reward.soulDust || 0);
        if (sd > 0) parts.push(`${sd.toLocaleString()} Soul Dust`);
        const basic = Number(reward.tickets?.basic || 0);
        if (basic > 0) parts.push(`${basic} Basic Ticket${basic > 1 ? 's' : ''}`);
        return parts.length ? parts.join(' + ') : '—';
    };

    // 🕒 Claim daily reward via Cloud Run HTTPS function (same pattern as Shop)
    const handleClaimDaily = async () => {
        if (!auth?.currentUser) return;
        if (!CF_BASES.claimArenaDailyReward) {
            console.error('[Arena] CF_BASES.claimArenaDailyReward is not configured');
            setDailyError('Daily reward endpoint is not configured.');
            return;
        }

        setClaimingDaily(true);
        setDailyError(null);

        try {
            const token = await auth.currentUser.getIdToken();
            const res = await fetchWithRetry(
                CF_BASES.claimArenaDailyReward,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                },
                2
            );

            const contentType = res.headers.get('content-type') || '';
            let data = {};
            if (contentType.includes('application/json')) {
                data = await res.json();
            } else {
                const txt = await res.text();
                throw new Error(`Unexpected response (not JSON): ${txt?.slice(0, 200)}`);
            }

            if (data.error) {
                if (data.error === 'cooldown' && data.nextAt) {
                    setNextDailyAt(Number(data.nextAt));
                }
                throw new Error(data.error);
            }

            if (data.nextAt) {
                setNextDailyAt(Number(data.nextAt));
            }
        } catch (e) {
            console.error('[Arena] claim daily error:', e);
            setDailyError(e.message || String(e));
        } finally {
            setClaimingDaily(false);
        }
    };

    const dailyReady = !nextDailyAt || nextDailyAt <= Date.now();

    const handleNextPage = () => {
        setCurrentPage((prev) => {
            const maxPage = Math.max(0, Math.ceil(allOpponents.length / OPPONENTS_PER_PAGE) - 1);
            return Math.min(prev + 1, maxPage);
        });
    };

    const handlePrevPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 0));
    };

    const loadLeaderboardFromBackend = useCallback(async () => {
        if (!auth?.currentUser) return;
        if (!CF_BASES.getArenaLeaderboard) {
            console.warn('[Arena] getArenaLeaderboard URL not configured');
            return;
        }

        setLeaderboardLoading(true);
        setLeaderboardError(null);

        try {
            const token = await auth.currentUser.getIdToken();
            const res = await fetchWithRetry(
                CF_BASES.getArenaLeaderboard,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                },
                2
            );

            const contentType = res.headers.get('content-type') || '';
            let data = {};
            if (contentType.includes('application/json')) {
                data = await res.json();
            } else {
                const txt = await res.text();
                throw new Error(`Unexpected response (not JSON): ${txt?.slice(0, 200)}`);
            }

            if (data.error) throw new Error(data.error);

            const top = Array.isArray(data.top) ? data.top : [];
            setLeaderboardRows(top);
            setGlobalRank(
                typeof data.myRank === 'number' && data.myRank > 0
                    ? data.myRank
                    : null
            );
            setGlobalPlayerCount(
                typeof data.totalPlayers === 'number' && data.totalPlayers >= 0
                    ? data.totalPlayers
                    : 0
            );
        } catch (e) {
            console.error('[Arena] load leaderboard error:', e);
            setLeaderboardError(e.message || String(e));
            setLeaderboardRows([]);
            setGlobalRank(null);
            setGlobalPlayerCount(0);
        } finally {
            setLeaderboardLoading(false);
        }
    }, []);

    // 🔁 Refresh global rank whenever trophies or uid change
    useEffect(() => {
        if (!userData) return;
        loadLeaderboardFromBackend();
    }, [userData?.uid, userData?.trophies, loadLeaderboardFromBackend]);

    if (loading || !userData || !globalCards) {
        return (
            <Box sx={{ p: 2, ...panelSx }}>
                <Typography sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif`, letterSpacing: '0.8px' }}>
                    Loading…
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        color: TEXT_WARM,
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '1px',
                        textShadow: `0 0 18px ${alpha(GOLD, 0.25)}`,
                    }}
                >
                    Battle Arena
                </Typography>

                {/* Row 1 */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Chip label={`League: ${displayLeague}`} sx={chipSolidSx} size="small" />
                    <Chip label={`Trophies: ${fmt(trophies)}`} sx={chipOutlineSx} size="small" />
                    <Chip
                        label={
                            playerGuildLoading
                                ? 'Guild: Loading…'
                                : `Guild: ${playerGuildName || 'No Guild'}`
                        }
                        sx={chipOutlineSx}
                        size="small"
                    />
                    <Chip
                        label={
                            leaderboardLoading
                                ? 'Global Rank: Loading…'
                                : globalPlayerCount > 0 && globalRank
                                    ? `Global Rank: #${fmt(globalRank)} / ${fmt(globalPlayerCount)}`
                                    : globalPlayerCount > 0
                                        ? `Global Rank: Unranked / ${fmt(globalPlayerCount)}`
                                        : 'Global Rank: —'
                        }
                        sx={chipOutlineSx}
                        size="small"
                    />
                    {next && (
                        <Typography variant="body2" sx={{ color: alpha(TEXT_WARM, 0.9) }}>
                            Next: <Box component="span" sx={{ color: GOLD }}>{next.name}</Box> at {next.minTrophies} ({toNext} to go)
                        </Typography>
                    )}

                    <DailyCountdownChip nextDailyAt={nextDailyAt} />

                    <Stack direction="row" spacing={1} sx={{ ml: 'auto' }} alignItems="center">
                        <Tooltip title={dailyReady ? 'Claim your arena daily reward' : 'Daily reward not ready yet'}>
                            <span>
                                <GoldButton
                                    onClick={handleClaimDaily}
                                    disabled={claimingDaily || !dailyReady || !auth?.currentUser}
                                >
                                    {claimingDaily ? 'Claiming…' : 'Claim Daily'}
                                </GoldButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Row 2 */}
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Tooltip title="View daily and quarterly arena rewards">
                        <span>
                            <GoldButton onClick={() => setRewardsOpen(true)}>
                                View Arena Rewards
                            </GoldButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="View the global top 1,000 Arena players">
                        <span>
                            <GoldButton
                                onClick={() => {
                                    loadLeaderboardFromBackend();
                                    setLeaderboardOpen(true);
                                }}
                            >
                                Global Leaderboard
                            </GoldButton>
                        </span>
                    </Tooltip>
                    <ArenaWinsEvent />
                </Stack>

                {dailyError && (
                    <Typography
                        variant="caption"
                        sx={{ mt: 0.5, display: 'block', color: alpha('#ff6b6b', 0.9) }}
                    >
                        Daily reward error: {dailyError}
                    </Typography>
                )}

                {leaderboardError && (
                    <Typography
                        variant="caption"
                        sx={{ mt: 0.5, display: 'block', color: alpha('#ff6b6b', 0.9) }}
                    >
                        Leaderboard error: {leaderboardError}
                    </Typography>
                )}
            </Box>

            {/* Player Deck Overview Panel */}
            <Box sx={{ ...panelSx, p: 2 }}>
                <DeckOverview />
            </Box>

            <Divider sx={{ my: 3, borderColor: alpha(GOLD, 0.25) }} />

            {/* Refresh row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Tooltip
                        title={
                            refreshReady
                                ? 'Arena refresh is ready – reroll opponents for free'
                                : soulDust >= SOUL_DUST_REFRESH_COST
                                    ? `Spend ${SOUL_DUST_REFRESH_COST} Soul Dust to reroll opponents early`
                                    : `Need ${SOUL_DUST_REFRESH_COST} Soul Dust to reroll opponents early`
                        }
                    >
                        <span>
                            <GoldButton
                                onClick={handleRefresh}
                                disabled={
                                    refreshing ||
                                    (!refreshReady && soulDust < SOUL_DUST_REFRESH_COST)
                                }
                            >
                                {refreshing
                                    ? 'Refreshing…'
                                    : refreshReady
                                        ? 'Free Refresh'
                                        : `Refresh (-${SOUL_DUST_REFRESH_COST} Soul Dust)`}
                            </GoldButton>
                        </span>
                    </Tooltip>

                    <AutoRefreshCountdownChip nextRefreshAt={nextRefreshAt} />
                </Stack>
            </Stack>

            {/* Opponents Header + pagination */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                        color: TEXT_WARM,
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '.8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0,
                    }}
                >
                    Opponents
                    {loadingOpponents ? (
                        <Chip label="Loading…" size="small" sx={{ ...chipOutlineSx, ml: 1 }} />
                    ) : null}
                </Typography>

                {allOpponents.length > OPPONENTS_PER_PAGE && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <GoldButton size="small" onClick={handlePrevPage} disabled={currentPage === 0}>
                            Prev
                        </GoldButton>
                        <GoldButton size="small" onClick={handleNextPage} disabled={currentPage >= totalPages - 1}>
                            Next
                        </GoldButton>
                        <Typography variant="caption" sx={{ color: alpha(TEXT_WARM, 0.8) }}>
                            Page {currentPage + 1} / {totalPages}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            {/* Opponents Column */}
            {pvpOpponents.length === 0 ? (
                <Box sx={{ ...panelSx, p: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ color: alpha(TEXT_WARM, 0.9) }}>
                        No nearby opponents found yet in <Box component="span" sx={{ color: GOLD }}>{displayLeague}</Box>.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2.25} sx={{ mb: 4 }}>
                    {pvpOpponents.map((op) => {
                        const isBeaten = beatenOpponents.has(op.enemyId);
                        const disabled = !auth?.currentUser?.uid || isBeaten;

                        return (
                            <Box key={op.enemyId} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <OpponentCard
                                    onClick={() => !disabled && handleEnemyClick(op)}
                                    sx={{
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.6 : 1,
                                        width: '100%',
                                        maxWidth: 720,
                                    }}
                                >
                                    {isBeaten && (
                                        <Chip
                                            label="Defeated"
                                            size="small"
                                            sx={{
                                                ...chipSolidSx,
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                px: 1,
                                            }}
                                        />
                                    )}
                                    <Stack spacing={1}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontFamily: `'Cinzel', serif`,
                                                letterSpacing: '.6px',
                                                color: TEXT_WARM,
                                            }}
                                        >
                                            {op.username}
                                        </Typography>

                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            <Chip size="small" label={`Guild • ${op.guild}`} sx={chipOutlineSx} />
                                            <Chip size="small" label={`League • ${op.league}`} sx={chipOutlineSx} />
                                        </Stack>

                                        <Stack direction="row" spacing={1.25}>
                                            <Box sx={{ ...panelSx, px: 1.25, py: 0.75, borderRadius: 1.5 }}>
                                                <Typography variant="caption" sx={{ color: GOLD_DIM, display: 'block' }}>Trophies</Typography>
                                                <Typography variant="body2" sx={{ color: TEXT_WARM }}>{fmt(op.trophies)}</Typography>
                                            </Box>
                                            <Box sx={{ ...panelSx, px: 1.25, py: 0.75, borderRadius: 1.5 }}>
                                                <Typography variant="caption" sx={{ color: GOLD_DIM, display: 'block' }}>Power</Typography>
                                                <Typography variant="body2" sx={{ color: TEXT_WARM }}>{fmt(op.powerLevel)}</Typography>
                                            </Box>
                                        </Stack>

                                        <Typography variant="caption" sx={{ color: alpha(TEXT_WARM, 0.8) }}>
                                            {!auth?.currentUser?.uid
                                                ? 'Sign in to battle'
                                                : isBeaten
                                                    ? 'You have already defeated this opponent. Use Arena Refresh to reroll opponents.'
                                                    : 'Tap to battle'}
                                        </Typography>
                                    </Stack>
                                </OpponentCard>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <CombatDialog
                open={Boolean(selectedEnemy)}
                onClose={handleCloseDialog}
                enemy={selectedEnemy}
                playerCards={activeCards}
                playerTrophies={trophies}
                onResolved={handleBattleResolved}
                playerId={userData?.uid}
                getImageForCard={getImageForCard} // 🔥 preloaded art for any opponent
            />

            {/* Global Leaderboard Dialog */}
            <ArenaLeaderBoard
                open={leaderboardOpen}
                onClose={() => setLeaderboardOpen(false)}
                rows={leaderboardRows}
                loading={leaderboardLoading}
                error={leaderboardError}
                globalRank={globalRank}
                totalPlayers={globalPlayerCount}
            />

            {/* Rewards Dialog */}
            <Dialog
                open={rewardsOpen}
                onClose={() => setRewardsOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
                        borderRadius: 2,
                        border: `1px solid ${alpha(GOLD, 0.4)}`,
                        boxShadow: `0 0 0 1px ${alpha(GOLD, 0.2)} inset, 0 18px 40px ${alpha(GOLD, 0.25)}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: TEXT_WARM,
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '.8px',
                        borderBottom: `1px solid ${alpha(GOLD, 0.3)}`,
                    }}
                >
                    Arena Rewards
                    <IconButton size="small" onClick={() => setRewardsOpen(false)}>
                        <CloseIcon sx={{ color: GOLD }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: alpha(TEXT_WARM, 0.9) }}>
                        Rewards are based on your current Arena League.{` `}
                        <Box component="span" sx={{ color: GOLD }}>
                            Daily rewards
                        </Box>{' '}
                        are granted once per day, and{' '}
                        <Box component="span" sx={{ color: GOLD }}>
                            quarterly rewards
                        </Box>{' '}
                        are granted at the end of each season, after which all players are reset to{' '}
                        <Box component="span" sx={{ color: GOLD }}>
                            0 trophies
                        </Box>.
                    </Typography>

                    <Box sx={{ borderRadius: 2, border: `1px solid ${alpha(GOLD, 0.4)}`, overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ background: alpha(GOLD, 0.15) }}>
                                    <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>
                                        League
                                    </TableCell>
                                    <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>
                                        Min Trophies
                                    </TableCell>
                                    <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>
                                        Daily Rewards
                                    </TableCell>
                                    <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>
                                        Quarterly Rewards
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ARENA_LEAGUES.map((lg) => (
                                    <TableRow
                                        key={lg.id}
                                        sx={{
                                            background: 'transparent',
                                            '&:nth-of-type(even)': {
                                                background: alpha('#000', 0.4),
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ color: TEXT_WARM }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        background: GOLD,
                                                        boxShadow: `0 0 8px ${alpha(GOLD, 0.7)}`,
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ color: TEXT_WARM }}>
                                                    {lg.name}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ color: TEXT_WARM }}>
                                            {lg.minTrophies.toLocaleString()}
                                        </TableCell>
                                        <TableCell sx={{ color: alpha(TEXT_WARM, 0.95) }}>
                                            {formatReward(lg.dailyRewards)}
                                        </TableCell>
                                        <TableCell sx={{ color: alpha(TEXT_WARM, 0.95) }}>
                                            {formatReward(lg.leagueRewards)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: `1px solid ${alpha(GOLD, 0.3)}`,
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <Button onClick={() => setRewardsOpen(false)} sx={{ color: TEXT_WARM, textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Arena;
